import {
	Button,
	Checkbox,
	FormControlLabel,
	Grid2,
	Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import {
	ErrorModal,
	getShiftByParameters,
	MenuOptionType,
	ShiftInfo,
	useContextMenuMESF,
	useMesfRealtime,
} from "@dexteel/mesf-core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import {
	Carrier,
	CARRIER_INITIAL_VALUES,
} from "@/pages/mill/carriers/models/Carrier";
import {
	routeCarrierAssembly,
	routeMillGantt,
} from "@/setup/routes/mill.routes";
import { WarningModal } from "@/shared/modals/WarningModal";
import { backgroundColor } from "@/utils/theme";
import { Leaderboard } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import LockIcon from "@mui/icons-material/Lock";
import RefreshIcon from "@mui/icons-material/Refresh";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { RowClassParams, RowStyle } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { useLastInstancesTableData } from "../hooks/useLastInstancesTableData";
import {
	getLastCarrierInstances,
	updateCarrierStatus,
} from "../repositories/CarrierAssemblyRepository";
import { ActionModal } from "./ActionModal";
import { assemblyStatusCodes } from "./CarrierAssemblyStatusTag";
import { CarrierCard } from "./CarrierCard";
import { PassChangeModal } from "./passes/PassChangeModal";

type useLastCarrierInstancesProps = {
	partId: number;
};

export const useLastCarrierInstances = ({
	partId,
}: useLastCarrierInstancesProps) => {
	return useQuery<Carrier[], Error>({
		queryKey: ["carrierInstances", partId],
		queryFn: ({ signal }) => getLastCarrierInstances({ partId }, signal),
	});
};

type Props = {
	carrier: Carrier | null;
	modal: "assembly" | "disassembly" | "";
	onHide: ({ close, entityId }: { close: boolean; entityId?: number }) => void;
};

export const CarrierAssemblyDetails = ({ carrier, modal, onHide }: Props) => {
	const navigate = useNavigate();
	const { showContextMenu, registerConfig } = useContextMenuMESF();

	const [action, setAction] = useState<Record<string, string>>({
		action: "install",
		code: "I",
		message: "installed",
	});
	const [message, setNotificationMessage] = useState<string>("");
	const [error, setError] = useState<string>("");
	const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
	const [openInstallUninstallDialog, setOpenInstallUninstallDialog] =
		useState(false);
	const [openPassChangeDialog, setOpenPassChangeDialog] = useState(false);
	const [showAllCarriers, setShowAllCarriers] = useState(false);
	const [warningMessage, setWarningMessage] = useState("");

	const { reset } = useForm<Carrier>({
		defaultValues: CARRIER_INITIAL_VALUES,
		mode: "onChange",
	});

	const {
		data: rows,
		error: e,
		isError,
		isLoading,
	} = useLastCarrierInstances({ partId: carrier?.PartId! });

	const { columnDefs } = useLastInstancesTableData({});
	const { defaultColDef } = useGridDefinitions({});

	const getMenuOptions = (data: Carrier) => {
		let options: MenuOptionType[] = [];
		if (data) {
			options = options.concat([
				{
					name: "See on Gantt",
					key: "see_instance_on_gantt",
					onClick: async () => {
						const resp = await getShiftByParameters({
							productionDate: moment(data.InstalledAt).toDate(),
							shiftId: 0,
						});
						if (resp.ok) {
							const shiftId = (resp.data as ShiftInfo).CurrentShiftId;
							window.open(
								`${routeMillGantt}/${shiftId}?carrierInstanceId=${data.CarrierInstanceId}`,
								"_blank",
							);
						} else {
							setError(resp.message);
						}
					},
					icon: <Leaderboard />,
				},
			]);
		}
		return options;
	};

	const getRowStyle = (params: RowClassParams): RowStyle | undefined => {
		if ((params.data as Carrier).CreatedAt === carrier?.CreatedAt) {
			return { backgroundColor };
		}
	};

	const queryClient = useQueryClient();
	const updateStatus = useMutation(
		({ newStatus }: { newStatus: string }) => {
			return updateCarrierStatus({
				partId: carrier?.PartId!,
				newStatus,
			});
		},
		{
			onSuccess: async () => {
				await queryClient.invalidateQueries(["carriersAssembly"]);
				await queryClient.invalidateQueries(["carrierInstances"]);
				setNotificationMessage(
					`The carrier was ${action["message"]} successfully`,
				);
				onHide({
					close: false,
				});
			},
			onError: (error: Error) => {
				setError(error.message);
			},
		},
	);

	useEffect(() => {
		if (modal !== "disassembly") {
			reset(carrier || CARRIER_INITIAL_VALUES);
		}
	}, [modal, carrier]);

	useMesfRealtime({
		onReceiveMessage: async (author, message) => {
			if (message.replaceAll("[", "").replaceAll("]", "") == "M2.GetCarriers") {
				await queryClient.invalidateQueries({ queryKey: ["carriersAssembly"] });
			}
		},
	});

	useEffect(() => {
		registerConfig({
			id: "lastInstancesContext",
			getOptions: getMenuOptions,
		});
	}, []);

	useEffect(() => {
		if (isError) {
			setError(e?.message);
		}
	}, [e, isError]);

	return (
		<>
			{carrier === null && modal !== "disassembly" ? (
				<div
					style={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						height: "100%",
						width: "100%",
					}}
				>
					<Typography>Select a carrier</Typography>
				</div>
			) : (
				<form>
					<Grid2
						container
						spacing={1}
						style={{ padding: 20 }}
						justifyContent="center"
					>
						<Grid2
							size={{ md: 6 }}
							data-test-id={`carrier-card-${assemblyStatusCodes
								.find((s) => s.Code === carrier?.CarrierStatusCode)
								?.Name.replaceAll(" ", "-")
								.toLowerCase()}`}
						>
							<CarrierCard carrier={carrier} />
						</Grid2>
						<Grid2
							size={{
								md: 1,
							}}
						/>
						<Grid2 container size={{ md: 5 }} spacing={1} alignItems="center">
							{carrier?.CarrierStatusCode === "A" && (
								<Grid2 size={{ md: 12 }}>
									<Button
										variant="contained"
										color="primary"
										fullWidth
										startIcon={<EditIcon />}
										onClick={() =>
											navigate(
												`/${routeCarrierAssembly}/${carrier?.PartId}/build/${carrier.CarrierInstanceId}`,
											)
										}
										data-test-id="edit-current-build-button"
									>
										Edit Current Build
									</Button>
								</Grid2>
							)}
							{carrier?.CarrierStatusCode === "C" && (
								<Grid2 size={{ md: 12 }}>
									<Button
										variant="contained"
										color="primary"
										fullWidth
										startIcon={<LockIcon />}
										onClick={() => {
											navigate(
												`/${routeCarrierAssembly}/${carrier?.PartId}/sign/${carrier.CarrierInstanceId}`,
											);
										}}
										data-test-id="sign-off-button"
									>
										Sign off
									</Button>
								</Grid2>
							)}

							{carrier?.CarrierStatusCode === "E" && (
								<Grid2 size={{ md: 12 }}>
									<Button
										variant="contained"
										color="primary"
										fullWidth
										onClick={() => {
											setAction({
												action: "build",
												code: "A",
												message: "built",
											});
											updateStatus.mutate({ newStatus: "A" });
										}}
										data-test-id="new-build-button"
									>
										New build
									</Button>
								</Grid2>
							)}
							{carrier?.CarrierStatusCode === "S" && (
								<Grid2 size={{ md: 12 }}>
									<Button
										variant="contained"
										color="primary"
										fullWidth
										onClick={() => {
											setAction({
												action: "install",
												code: "I",
												message: "installed",
											});
											setOpenInstallUninstallDialog(true);
										}}
										data-test-id="install-button"
									>
										Install
									</Button>
								</Grid2>
							)}
							{carrier?.CarrierStatusCode === "I" && (
								<>
									{(!carrier.TopRollName?.includes("UR") ||
										!carrier.BottomRollName?.includes("UR")) && (
										<Grid2 size={{ md: 12 }}>
											<Button
												variant="contained"
												color="primary"
												fullWidth
												startIcon={<SwapHorizIcon />}
												onClick={() => setOpenPassChangeDialog(true)}
												data-test-id="pass-change-button"
											>
												Pass Change
											</Button>
										</Grid2>
									)}
									<Grid2 size={{ md: 12 }}>
										<Button
											variant="outlined"
											color="secondary"
											fullWidth
											onClick={() => {
												setAction({
													action: "uninstall",
													code: "U",
													message: "uninstalled",
												});
												setOpenInstallUninstallDialog(true);
											}}
											data-test-id="uninstall-button"
										>
											Uninstall
										</Button>
									</Grid2>
								</>
							)}
							{carrier?.CarrierStatusCode !== "E" &&
								carrier?.CarrierStatusCode !== "I" &&
								carrier?.CarrierStatusCode != "U" && (
									<Grid2 size={{ md: 12 }}>
										<Button
											variant="contained"
											color="inherit"
											fullWidth
											startIcon={<RefreshIcon />}
											onClick={() => {
												setAction({
													action: "restart",
													code: "A",
													message: "restarted",
												});
												setOpenConfirmDialog(true);
											}}
											data-test-id="restart-build-button"
										>
											Restart Build
										</Button>
									</Grid2>
								)}
							{carrier?.CarrierStatusCode == "U" && (
								<Grid2 size={{ md: 12 }}>
									<Button
										variant="outlined"
										fullWidth
										startIcon={<RefreshIcon />}
										color="primary"
										onClick={() => {
											setAction({
												action: "disassemble",
												code: "F",
												message: "disassembled",
											});
											setOpenConfirmDialog(true);
										}}
										data-test-id="disassemble-build-button"
									>
										Disassemble Build
									</Button>
								</Grid2>
							)}
						</Grid2>
						<Grid2
							container
							size={12}
							justifyContent="space-between"
							alignItems="center"
						>
							<Grid2>
								<Typography variant="h6" sx={{ fontWeight: "bold" }}>
									Last Carrier Instances
								</Typography>
								<Typography
									variant="body2"
									sx={{ fontSize: "0.7rem", mt: -0.5 }}
									color="textSecondary"
								>
									Double click on a row to view the form
								</Typography>
							</Grid2>
							<Grid2 justifyContent="flex-end" display="flex">
								<FormControlLabel
									control={
										<Checkbox
											checked={showAllCarriers}
											onChange={(e, checked) => setShowAllCarriers(checked)}
											size="small"
										/>
									}
									label="Show all"
								/>
							</Grid2>
						</Grid2>
						<Grid2 size={{ md: 12 }} style={{ height: "45vh" }}>
							<AgGridReact<Carrier>
								loading={isLoading}
								rowData={
									rows?.filter((r) => {
										if (showAllCarriers) {
											return true;
										}
										return (
											r?.InstalledAt != null ||
											r.CarrierInstanceId == carrier?.CarrierInstanceId
										);
									}) || []
								}
								columnDefs={columnDefs}
								defaultColDef={defaultColDef}
								rowHeight={34}
								headerHeight={34}
								animateRows
								getRowId={(params) => String(params.data.CreatedAt)}
								getRowStyle={getRowStyle}
								onRowDoubleClicked={(params) => {
									navigate(
										`/${routeCarrierAssembly}/${params.data?.PartId}/view/${params.data?.CarrierInstanceId}`,
									);
								}}
								getContextMenuItems={(params) => {
									const data = params.node?.data;
									showContextMenu(event as any, data, "lastInstancesContext");
									return [];
								}}
								loadingOverlayComponent={CenteredLazyLoading}
								gridOptions={{
									rowSelection: {
										mode: "singleRow",
										checkboxes: false,
										enableClickSelection: false,
										isRowSelectable: () => false,
									},
								}}
							/>
						</Grid2>
					</Grid2>
				</form>
			)}
			<ConfirmationDialogRaw
				title={`CONFIRM ${action["action"].toUpperCase()}`}
				open={openConfirmDialog}
				message={`Are you sure you want to ${action["action"]} the carrier?`}
				onClose={(value?: string) => {
					if (value == "OK") {
						updateStatus.mutate({ newStatus: action["code"] });
					}
					setOpenConfirmDialog(false);
				}}
			/>
			<NotificationSnackBar
				message={message}
				onHide={() => setNotificationMessage("")}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
			<ActionModal
				open={openInstallUninstallDialog}
				onHide={() => setOpenInstallUninstallDialog(false)}
				setNotificationMessage={setNotificationMessage}
				partId={carrier?.PartId!}
				partName={carrier?.PartName!}
				action={action}
				setWarningMessage={setWarningMessage}
			/>
			<WarningModal
				open={warningMessage !== ""}
				title="Warning"
				message={warningMessage}
				onClose={() => setWarningMessage("")}
				suffixTitle="Carrier Status"
			/>

			<PassChangeModal
				open={openPassChangeDialog}
				onHide={() => setOpenPassChangeDialog(false)}
				setNotificationMessage={setNotificationMessage}
				carrier={{
					partId: carrier?.PartId!,
					carrierInstanceId: carrier?.CarrierInstanceId!,
				}}
			/>
		</>
	);
};
